import React, { useMemo } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { A11y, Navigation } from 'swiper/modules'
import useTrans from '@hooks/useTrans'
import { cn, swiperA11yMessages } from '@utils/safeTs'
import { EditorImageType, EditorStatusType, EditorTextType, PosizioneType } from '@utils/types'
import { WImage } from '@components/atoms'
import styles from './EditorArticoliPreviews.module.sass'
import { useEditorContext } from 'context/EditorContext'

interface Props {
  status?: EditorStatusType
  selectedPosizione: PosizioneType & { images?: EditorImageType[]; texts?: EditorTextType[] }
  onArticoloPrincipaleChange?: (coloreId: number) => void
  className?: string
}

const EditorArticoliPreviews = (props: Props) => {
  const {
    status,
    selectedPosizione,
    onArticoloPrincipaleChange,
    className = '',
  } = props

  // TODO - qui capire come rendere la preview: si fa un'immagine ad hoc o mettiamo in position absolute tutti gli elementi impostati nell'editor?
  // Per ora semplice immagine main

  const t = useTrans()
  const { activeCanvas } = useEditorContext()

  const otherArticoli = useMemo(() => {
    const mainColorId = status.articoloPrincipale?.colore?.id
    const allArticoli = status?.articoli || []

    // Filtriamo: teniamo solo gli articoli con colore DIVERSO dal principale
    const filteredByColor = allArticoli.filter((a) => a.colore?.id !== mainColorId)

    // Rendiamo distinti: una sola occorrenza per ogni ID colore (usiamo la Map per togliere i duplicati)
    const uniqueOtherArticoli = Array.from(
      new Map(filteredByColor.map((item) => [item.colore?.id, item])).values()
    )

    return uniqueOtherArticoli
  }, [status?.articoli, status.articoloPrincipale?.colore?.id])

  const getImage = (articolo) => {
    return status?.posizioni
      ?.find((p) => p.id === selectedPosizione.id)
      ?.configurazione?.aree_stampa?.find((a) => a.colore?.id === articolo.colore?.id)
      ?.immagine_originale
  }

  const getOverlay = (articolo: any) => {
    const colorId = articolo?.colore?.id
    if (colorId === undefined || colorId === null) return activeCanvas?.preview

    return activeCanvas?.previewByColorId?.[String(colorId)] || activeCanvas?.preview
  }

  return otherArticoli?.length > 0 ? (
    <div className={cn(styles.root, className)}>
      <h2 className={styles.title}>{t('Anteprima colori')}</h2>
      <Swiper
        modules={[A11y, Navigation]}
        a11y={swiperA11yMessages(t)}
        autoplay={{ delay: 5000 }}
        navigation={true}
        slidesPerView={'auto'}
        direction="vertical"
      >
        {otherArticoli.map((a) => (
          <SwiperSlide key={`editorarticolipreviews-slide-${a.id}`}>
            <button
              type="button"
              className={styles.item}
              disabled={!onArticoloPrincipaleChange}
              onClick={() =>
                typeof a?.colore?.id === 'number' ? onArticoloPrincipaleChange?.(a.colore.id) : null
              }
              aria-label={a?.colore?.nome || t('Imposta colore principale')}
            >
              {!!getImage(a) && (
                <WImage src={getImage(a).url} maxWidth={180} maxHeight={180} objectFit="contain" />
              )}
              {activeCanvas?.preview && (
                <img src={getOverlay(a)} className={styles.overlay} />
              )}
            </button>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  ) : (
    <></>
  )
}

export default EditorArticoliPreviews
